/**
 * VELARE Animations
 * Vanilla JS replacement for Framer Motion — Shopify theme compatible.
 * No dependencies. Loaded via <script> tag.
 *
 * Data attributes consumed:
 *   [data-animate="fade-in"]   — Fade + slide up on scroll
 *   [data-delay="0.2"]         — Stagger delay in seconds
 *   [data-parallax="hero"]     — Hero parallax (translateY)
 *   [data-parallax="zoom"]     — Section zoom on scroll (scale)
 *   [data-navbar]              — Scroll-aware navbar
 *   [data-menu-toggle]         — Mobile menu open/close
 *   [data-mobile-menu]         — Mobile menu panel
 *   [data-bounce-arrow]        — Bouncing scroll indicator
 *
 * CSS classes toggled:
 *   .is-visible   — Applied to fade-in elements when in view
 *   .is-scrolled  — Applied to navbar when scrollY > 50
 *   .is-open      — Applied to mobile menu when toggled
 */
;(function () {
  'use strict'

  /* ------------------------------------------------------------------ */
  /*  Utility helpers                                                    */
  /* ------------------------------------------------------------------ */

  var raf = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16) }
  var ticking = false

  /**
   * Clamp a number between min and max.
   */
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max)
  }

  /**
   * Linear interpolation between a and b at ratio t.
   */
  function lerp(a, b, t) {
    return a + (b - a) * t
  }

  /**
   * Return the ratio (0-1) of how far the element has scrolled through
   * the viewport. 0 = element top just entered the bottom, 1 = element
   * bottom just left the top.
   */
  function scrollProgress(el) {
    var rect = el.getBoundingClientRect()
    var winH = window.innerHeight
    // progress: 0 when el top is at viewport bottom, 1 when el bottom is at viewport top
    var raw = (winH - rect.top) / (winH + rect.height)
    return clamp(raw, 0, 1)
  }

  /* ------------------------------------------------------------------ */
  /*  1. Fade-in on scroll (IntersectionObserver)                       */
  /* ------------------------------------------------------------------ */

  function initFadeIn() {
    var elements = document.querySelectorAll('[data-animate="fade-in"]')
    if (!elements.length) return

    // Apply initial hidden styles via JS so content is still visible
    // without JS (progressive enhancement).
    for (var i = 0; i < elements.length; i++) {
      var el = elements[i]
      var delay = parseFloat(el.getAttribute('data-delay')) || 0
      el.style.opacity = '0'
      el.style.transform = 'translateY(20px)'
      el.style.transition =
        'opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1) ' + delay + 's, ' +
        'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1) ' + delay + 's'
      el.style.willChange = 'opacity, transform'
    }

    if (!('IntersectionObserver' in window)) {
      // Fallback: reveal everything immediately.
      for (var j = 0; j < elements.length; j++) {
        revealElement(elements[j])
      }
      return
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            revealElement(entry.target)
            observer.unobserve(entry.target) // once: true
          }
        })
      },
      { threshold: 0.15 }
    )

    elements.forEach(function (el) {
      observer.observe(el)
    })
  }

  function revealElement(el) {
    el.style.opacity = '1'
    el.style.transform = 'translateY(0)'
    el.classList.add('is-visible')

    // Clean up will-change after animation completes.
    var delay = (parseFloat(el.getAttribute('data-delay')) || 0) * 1000
    setTimeout(function () {
      el.style.willChange = ''
    }, 800 + delay + 50)
  }

  /* ------------------------------------------------------------------ */
  /*  2. Hero parallax                                                  */
  /* ------------------------------------------------------------------ */

  var heroEl = null
  var heroSection = null

  function initHeroParallax() {
    heroEl = document.querySelector('[data-parallax="hero"]')
    if (!heroEl) return
    // The hero section is the closest ancestor section, or fallback to parent.
    heroSection = heroEl.closest('section') || heroEl.parentElement
  }

  function updateHeroParallax() {
    if (!heroEl || !heroSection) return

    var rect = heroSection.getBoundingClientRect()
    var winH = window.innerHeight

    // Only update when the hero section is at least partially in the viewport.
    if (rect.bottom < 0 || rect.top > winH) return

    // Progress 0 = section at initial position, 1 = section fully scrolled past.
    var progress = clamp(-rect.top / rect.height, 0, 1)

    // Translate at 22% of section height max, matching Framer Motion's
    // useTransform(scrollYProgress, [0, 1], ["0%", "22%"])
    heroEl.style.transform = 'translateY(' + (progress * 22) + '%)'
  }

  /* ------------------------------------------------------------------ */
  /*  3. Atmosphere zoom                                                */
  /* ------------------------------------------------------------------ */

  var zoomElements = []

  function initAtmosphereZoom() {
    var els = document.querySelectorAll('[data-parallax="zoom"]')
    if (!els.length) return
    zoomElements = Array.prototype.slice.call(els)

    // Set initial scale and will-change for GPU compositing.
    zoomElements.forEach(function (el) {
      el.style.willChange = 'transform'
      el.style.transform = 'scale(1)'
    })
  }

  function updateAtmosphereZoom() {
    if (!zoomElements.length) return

    zoomElements.forEach(function (el) {
      var section = el.closest('section') || el.parentElement
      var rect = section.getBoundingClientRect()
      var winH = window.innerHeight

      // Skip if completely off-screen.
      if (rect.bottom < 0 || rect.top > winH) return

      // progress: 0 at entry, 0.5 when section is centered, 1 at exit.
      var progress = scrollProgress(section)

      // Scale from 1.0 to 1.08. We use only the first half of scroll
      // progress (0 -> 0.5 maps to 0 -> 1.08) matching the React version:
      // useTransform(scrollYProgress, [0, 0.5], [1, 1.08])
      var t = clamp(progress / 0.5, 0, 1)
      var scale = lerp(1.0, 1.08, t)

      el.style.transform = 'scale(' + scale + ')'
    })
  }

  /* ------------------------------------------------------------------ */
  /*  4. Navbar scroll state                                            */
  /* ------------------------------------------------------------------ */

  var navbarEl = null

  function initNavbar() {
    navbarEl = document.querySelector('[data-navbar]')
    if (!navbarEl) return

    // Ensure the navbar has a smooth transition for background/border changes.
    // This won't override existing CSS transitions; it merely guarantees one exists.
    var existing = window.getComputedStyle(navbarEl).transition
    if (!existing || existing === 'all 0s ease 0s') {
      navbarEl.style.transition = 'background-color 0.3s ease, border-color 0.3s ease, backdrop-filter 0.3s ease'
    }

    // Set initial state.
    updateNavbar()
  }

  function updateNavbar() {
    if (!navbarEl) return

    if (window.scrollY > 50) {
      if (!navbarEl.classList.contains('is-scrolled')) {
        navbarEl.classList.add('is-scrolled')
      }
    } else {
      navbarEl.classList.remove('is-scrolled')
    }
  }

  /* ------------------------------------------------------------------ */
  /*  5. Bouncing arrow                                                 */
  /*  (CSS-driven; JS only ensures it hides on mobile via matchMedia)   */
  /* ------------------------------------------------------------------ */

  function initBounceArrow() {
    var arrow = document.querySelector('[data-bounce-arrow]')
    if (!arrow) return

    // Inject keyframes if not already present.
    if (!document.getElementById('velare-bounce-kf')) {
      var style = document.createElement('style')
      style.id = 'velare-bounce-kf'
      style.textContent =
        '@keyframes velare-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(10px)}}' +
        '[data-bounce-arrow]{animation:velare-bounce 2.5s ease-in-out infinite}' +
        '@media(max-width:767px){[data-bounce-arrow]{display:none!important}}'
      document.head.appendChild(style)
    }
  }

  /* ------------------------------------------------------------------ */
  /*  6. Mobile menu toggle                                             */
  /* ------------------------------------------------------------------ */

  function initMobileMenu() {
    var toggle = document.querySelector('[data-menu-toggle]')
    var menu = document.querySelector('[data-mobile-menu]')
    if (!toggle || !menu) return

    toggle.addEventListener('click', function () {
      var isOpen = menu.classList.toggle('is-open')
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false')

      // Prevent body scroll while menu is open.
      document.body.style.overflow = isOpen ? 'hidden' : ''
    })

    // Close menu when a nav link is clicked.
    var links = menu.querySelectorAll('a')
    for (var i = 0; i < links.length; i++) {
      links[i].addEventListener('click', function () {
        menu.classList.remove('is-open')
        toggle.setAttribute('aria-expanded', 'false')
        document.body.style.overflow = ''
      })
    }
  }

  /* ------------------------------------------------------------------ */
  /*  7. Smooth scroll for anchor links                                 */
  /* ------------------------------------------------------------------ */

  function initSmoothScroll() {
    // Determine fixed navbar height for offset calculation.
    var getNavbarHeight = function () {
      var nav = document.querySelector('[data-navbar]')
      if (!nav) return 0
      return nav.offsetHeight
    }

    document.addEventListener('click', function (e) {
      // Use closest() so clicks on child elements (icons, spans) inside
      // anchor tags are also captured.
      var link = e.target.closest('a[href^="#"]')
      if (!link) return

      var href = link.getAttribute('href')
      if (!href || href === '#') return

      var target = document.querySelector(href)
      if (!target) return

      e.preventDefault()

      var offset = getNavbarHeight()
      var targetTop = target.getBoundingClientRect().top + window.pageYOffset - offset

      window.scrollTo({
        top: targetTop,
        behavior: 'smooth',
      })

      // Update URL hash without jumping.
      if (history.pushState) {
        history.pushState(null, '', href)
      }
    })
  }

  /* ------------------------------------------------------------------ */
  /*  Scroll loop (rAF-throttled)                                       */
  /* ------------------------------------------------------------------ */

  function onScroll() {
    if (ticking) return
    ticking = true

    raf(function () {
      updateHeroParallax()
      updateAtmosphereZoom()
      updateNavbar()
      ticking = false
    })
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                              */
  /* ------------------------------------------------------------------ */

  /* ------------------------------------------------------------------ */
  /*  8. Safari video autoplay fix                                      */
  /* ------------------------------------------------------------------ */

  function initHeroVideo() {
    var videos = document.querySelectorAll('[data-hero-video]')
    if (!videos.length) return

    videos.forEach(function (video) {
      // Ensure muted (Safari requires this for autoplay)
      video.muted = true
      video.setAttribute('muted', '')
      video.setAttribute('playsinline', '')

      // Try to play immediately
      var playPromise = video.play()

      if (playPromise !== undefined) {
        playPromise.catch(function () {
          // Autoplay blocked — try again on first user interaction
          var tryPlay = function () {
            video.muted = true
            video.play().catch(function () {})
            document.removeEventListener('touchstart', tryPlay)
            document.removeEventListener('click', tryPlay)
            document.removeEventListener('scroll', tryPlay)
          }
          document.addEventListener('touchstart', tryPlay, { once: true, passive: true })
          document.addEventListener('click', tryPlay, { once: true })
          document.addEventListener('scroll', tryPlay, { once: true, passive: true })
        })
      }
    })
  }

  function init() {
    initFadeIn()
    initHeroParallax()
    initAtmosphereZoom()
    initNavbar()
    initBounceArrow()
    initMobileMenu()
    initSmoothScroll()
    initHeroVideo()

    // Single scroll listener, passive for performance.
    window.addEventListener('scroll', onScroll, { passive: true })

    // Run once to set initial state (e.g. page reloaded while scrolled).
    onScroll()
  }

  // Boot when DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
